import { useEffect, useRef } from "react";
import { captureException } from "@sentry/nextjs";
import { PropertyDataV2 } from "commonModule/services/common";
import { CountApiListOfAllProperties } from "commonModule/services/search";
import InfoPopup from "components/ListingMap/InfoPopup";
import useFavorites from "hooks/swr/useFavorites";
import { apiClientCommon } from "services/apiClientCommon";
import { useAppDispatch, useAppSelector } from "stores/hooks";
import { selectSearch } from "stores/search/searchSlice";
import { markUI, selectOpenedPropertyCardInMapBox } from "stores/ui/uiSlice";
import { getCookie } from "utils/cookies";
const HighlightedPropertyBoxMobile = ({
  properties = [],
  propertyListWithLatLng,
  propertyData,
  getPropertyData,
  multiUnitCollapsedEnabled = true
}: {
  properties?: PropertyDataV2[];
  propertyListWithLatLng: CountApiListOfAllProperties[];
  propertyData?: PropertyDataV2[];
  getPropertyData?: React.Dispatch<React.SetStateAction<PropertyDataV2[]>>;
  multiUnitCollapsedEnabled?: boolean;
}) => {
  const openedPropertyCardInMapBox = useAppSelector(selectOpenedPropertyCardInMapBox);
  const search = useAppSelector(selectSearch);
  const unitDataLengthMobileRef = useRef(0);
  const pageMobileRef = useRef(0);
  const property = properties?.find(_prop => _prop._id === openedPropertyCardInMapBox);
  const PAGE_SIZE = 10;
  const isgoogle = getCookie("isGoogle") === "true";

  // A property should only be shown as multi-unit if there is more than one unit available.
  // If only one unit exists, it should display as a single property directly, without showing a multi-unit card.
  const matchedProperty = propertyListWithLatLng?.find(item => item.id === openedPropertyCardInMapBox // Match property ID with count API response id
  );
  // if count > 1, set hasMultiple to show multi-unit property items
  const hasMultiple = !multiUnitCollapsedEnabled ? true : (matchedProperty?.count || 0) > 1;
  const isMultiUnitProperty = !!(property?.multiUnitGrp && property?.multiUnitCount && property?.multiUnitCount > 1 && hasMultiple);
  const favPropertyIds = useFavorites();
  const isFavourite = !!favPropertyIds.find(id => id === property?._id);
  const dispatch = useAppDispatch();
  useEffect(() => {
    unitDataLengthMobileRef.current = propertyData?.length || 0;
  }, [propertyData?.length]);
  useEffect(() => {
    if ((!propertyData || propertyData.length === 0) && openedPropertyCardInMapBox === property?._id && isMultiUnitProperty) {
      dispatch(markUI({
        openedPropertyCardInMapBox: null
      }));
    }
  }, [propertyData]);
  const onOpen = () => {
    dispatch(markUI({
      openedPropertyCardInMapBox: property?._id
    }));
  };
  const onClose = () => {
    dispatch(markUI({
      openedPropertyCardInMapBox: null
    }));
    pageMobileRef.current = 0;
    unitDataLengthMobileRef.current = 0;
    if (getPropertyData) {
      getPropertyData([]);
    }
  };
  if (!property?._id) return null;
  const propertyIndex = properties.findIndex(prop => prop._id === property._id);
  const normalizeToArray = (data: PropertyDataV2 | PropertyDataV2[]): PropertyDataV2[] => {
    return Array.isArray(data) ? data : [data];
  };
  const fetchPropertyCard = (newPage: number) => {
    if (!property?._id) return; // Guard clause to ensure property._id is available

    void apiClientCommon.searchDotPropertyCard({
      params: {
        id: property._id,
        noOfDays: search?.noOfDays || 0,
        startDate: search?.startDate || "",
        numberOfGuest: search?.numberOfGuest || 0,
        petAllowed: search?.petAllowed || false,
        isMultiUnit: true,
        allInPriceToggle: true,
        isgoogle,
        page: newPage,
        limit: PAGE_SIZE
      }
    }).then(response => {
      if (response?.status === 200) {
        const units = normalizeToArray(response.data.data);
        // when user click on dot button then we need to update the openedPropertyCardInMapBox state
        // so open then info popup
        dispatch(markUI({
          openedPropertyCardInMapBox: units[0]._id
        }));
        pageMobileRef.current = newPage;
        getPropertyData?.(prev => [...prev, ...units]);
        onOpen(); // open the property popup
      }
      return true;
    }).catch(err => {
      console.error(err);
      captureException(new Error(`Error 'searchPriceMultiUnitPropertyCard' ${JSON.stringify(err)}`));
    });
  };
  return <div className='z-1 absolute bottom-4 left-4 right-4 flex min-h-[280px] items-center justify-center sxm:left-0 sxm:right-0 sxm:w-full xl:hidden' key={property._id} data-sentry-component="HighlightedPropertyBoxMobile" data-sentry-source-file="HighlightedPropertyBoxMobile.tsx">
      <InfoPopup property={property} featuredUnitData={propertyData} isFavourite={isFavourite} onClose={onClose} pageOrigin='map - clicked price' propertyIndex={propertyIndex} isPriceMarker={true} hasMultiple={hasMultiple} actualPropertyId={`${property._id}`} fetchPropertyCard={fetchPropertyCard} unitDataLengthRef={unitDataLengthMobileRef} pageRef={pageMobileRef} data-sentry-element="InfoPopup" data-sentry-source-file="HighlightedPropertyBoxMobile.tsx" />
    </div>;
};
export default HighlightedPropertyBoxMobile;